import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download } from 'lucide-react';
import { useState } from 'react';
import {
  AmountHeads,
  DocLink,
  HeadCells,
  MonthPicker,
  Section,
} from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import {
  downloadReturnJson,
  lastMonth,
  monthLabel,
  periodRange,
  stateLabel,
  useGstr1,
  type GstInvoice,
  type Gstr1View,
} from '@/lib/gst';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/taxes/gst/gstr1')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Gstr1Page,
});

const NIL_LABELS: Record<string, string> = {
  INTRB2B: 'Inter-state supplies to registered persons',
  INTRAB2B: 'Intra-state supplies to registered persons',
  INTRB2C: 'Inter-state supplies to unregistered persons',
  INTRAB2C: 'Intra-state supplies to unregistered persons',
};

/** Screen T7 (document 06): GSTR-1 for a month. */
function Gstr1Page() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const period = Route.useSearch().period ?? lastMonth();
  const r = useGstr1(period);
  const [busy, setBusy] = useState(false);
  const { from, to } = periodRange(period);

  return (
    <>
      <PageHeader
        title="GSTR-1"
        description="Outward supplies for the month, by GSTR-1 table, from the posted documents."
        actions={
          <>
            <MonthPicker
              period={period}
              onChange={(p) => void navigate({ to: '/taxes/gst/gstr1', search: { period: p } })}
            />
            <Button asChild variant="outline">
              <Link
                to="/reports/$reportKey"
                params={{ reportKey: 'gstr-1' }}
                search={{ from, to } as never}
              >
                Open as report
              </Link>
            </Button>
            <Button
              disabled={!r.data?.gstin || busy}
              title={r.data && !r.data.gstin ? 'Add the GSTIN in Company settings' : undefined}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadReturnJson(orgId!, 'gstr1', period, r.data!.gstin!);
                } catch (e) {
                  toast((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Download className="size-4" /> Download JSON
            </Button>
          </>
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          GST
        </Link>{' '}
        › {monthLabel(period)}
        {r.data ? ` · due ${displayDate(r.data.dueDate)}` : ''}
      </p>
      {r.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : r.isError ? (
        <EmptyState title="Could not compute GSTR-1" hint={r.error.message} />
      ) : (
        <Gstr1Body r={r.data} />
      )}
    </>
  );
}

function Gstr1Body({ r }: { r: Gstr1View }) {
  const kind = (id: string | null) => (id ? (r.entries[id]?.kind ?? 'invoice') : 'invoice');
  const invoices = (title: string, list: GstInvoice[], testId: string) =>
    list.length ? (
      <Section title={title} testId={testId}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>No.</TableHead>
              <TableHead className="w-[110px]">Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="w-[170px]">GSTIN</TableHead>
              <TableHead className="w-[170px]">Place of supply</TableHead>
              <TableHead className="w-[120px] text-right">Value</TableHead>
              <AmountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((i) => (
              <TableRow key={`${i.documentId}-${i.number}`}>
                <TableCell>
                  {i.documentId ? (
                    <DocLink id={i.documentId} kind={kind(i.documentId)}>
                      {i.number}
                    </DocLink>
                  ) : (
                    i.number
                  )}
                </TableCell>
                <TableCell>{displayDate(i.date)}</TableCell>
                <TableCell>{i.contactName}</TableCell>
                <TableCell className="tnum">{i.gstin ?? ''}</TableCell>
                <TableCell>{stateLabel(i.placeOfSupply)}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(i.value)}</TableCell>
                <HeadCells a={i.totals} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    ) : null;

  return (
    <div className="space-y-6">
      {r.findings.length ? (
        <div
          className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm"
          role="note"
        >
          <p className="mb-1 inline-flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 text-[#B45309]" aria-hidden /> {r.findings.length}{' '}
            exception
            {r.findings.length === 1 ? '' : 's'} to review before filing
          </p>
          <ul className="list-disc pl-5">
            {r.findings.map((f, n) => (
              <li key={n}>{f.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <Section title="Summary" testId="gstr1-summary">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Table</TableHead>
              <TableHead>Supplies</TableHead>
              <TableHead className="w-[80px] text-right">Count</TableHead>
              <TableHead className="w-[130px] text-right">Value</TableHead>
              <AmountHeads />
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.summary.map((s) => (
              <TableRow key={`${s.table}-${s.label}`}>
                <TableCell>{s.table}</TableCell>
                <TableCell>{s.label}</TableCell>
                <TableCell className="tnum text-right">{s.count}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(s.value)}</TableCell>
                <HeadCells a={s} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      {invoices('4A · B2B invoices', r.b2b, 'gstr1-b2b')}
      {invoices('5 · B2C (large) invoices', r.b2cl, 'gstr1-b2cl')}
      {invoices('6A · Exports', r.exp, 'gstr1-exp')}
      {r.b2cs.length ? (
        <Section title="7 · B2C (others)" testId="gstr1-b2cs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Place of supply</TableHead>
                <TableHead className="w-[120px]">Supply</TableHead>
                <TableHead className="w-[80px] text-right">Rate</TableHead>
                <AmountHeads />
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.b2cs.map((x) => (
                <TableRow key={`${x.supplyType}-${x.placeOfSupply}-${x.rate}`}>
                  <TableCell>{stateLabel(x.placeOfSupply)}</TableCell>
                  <TableCell>{x.supplyType === 'INTER' ? 'Inter-state' : 'Intra-state'}</TableCell>
                  <TableCell className="tnum text-right">{rate(x.rate)}</TableCell>
                  <HeadCells a={x} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
      {r.nil.length ? (
        <Section title="8 · Nil rated, exempted and non-GST supplies" testId="gstr1-nil">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplies</TableHead>
                <TableHead className="w-[130px] text-right">Nil rated</TableHead>
                <TableHead className="w-[130px] text-right">Exempted</TableHead>
                <TableHead className="w-[130px] text-right">Non-GST</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.nil.map((x) => (
                <TableRow key={x.supplyType}>
                  <TableCell>{NIL_LABELS[x.supplyType] ?? x.supplyType}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(x.nil)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(x.exempt)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(x.nonGst)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
      {invoices('9B · Credit and debit notes (registered)', r.cdnr, 'gstr1-cdnr')}
      {invoices('9B · Credit and debit notes (unregistered)', r.cdnur, 'gstr1-cdnur')}

      {(['b2b', 'b2c'] as const).map((k) =>
        r.hsn[k].length ? (
          <Section
            key={k}
            title={`12 · HSN/SAC summary (${k === 'b2b' ? 'B2B' : 'B2C'})`}
            testId={`gstr1-hsn-${k}`}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">HSN/SAC</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[60px]">UQC</TableHead>
                  <TableHead className="w-[100px] text-right">Quantity</TableHead>
                  <TableHead className="w-[70px] text-right">Rate</TableHead>
                  <AmountHeads />
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.hsn[k].map((h) => (
                  <TableRow key={`${h.hsn}-${h.rate}-${h.uqc}`}>
                    <TableCell className="tnum">{h.hsn || '—'}</TableCell>
                    <TableCell>{h.description}</TableCell>
                    <TableCell>{h.uqc}</TableCell>
                    <TableCell className="tnum text-right">{h.quantity}</TableCell>
                    <TableCell className="tnum text-right">{rate(h.rate)}</TableCell>
                    <HeadCells a={h} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        ) : null,
      )}

      {r.docs.length ? (
        <Section title="13 · Documents issued" testId="gstr1-docs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nature of document</TableHead>
                <TableHead className="w-[120px]">From</TableHead>
                <TableHead className="w-[120px]">To</TableHead>
                <TableHead className="w-[90px] text-right">Total</TableHead>
                <TableHead className="w-[90px] text-right">Cancelled</TableHead>
                <TableHead className="w-[90px] text-right">Net issued</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.docs.map((d) => (
                <TableRow key={d.docType}>
                  <TableCell>{d.label}</TableCell>
                  <TableCell>{d.from}</TableCell>
                  <TableCell>{d.to}</TableCell>
                  <TableCell className="tnum text-right">{d.total}</TableCell>
                  <TableCell className="tnum text-right">{d.cancelled}</TableCell>
                  <TableCell className="tnum text-right">{d.net}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ) : null}
    </div>
  );
}

const rate = (r: string) => `${r.replace(/\.?0+$/, '')}%`;
